import CachedIcon from "@mui/icons-material/Cached";
import KeyboardBackspaceIcon from "@mui/icons-material/KeyboardBackspace";
import ModeEditOutlinedIcon from "@mui/icons-material/ModeEditOutlined";
import { Box, Divider, Typography } from "@mui/material";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import { styled } from "@mui/material/styles";
import { CloudIcon } from "@platformx/shared/static-assets";
import { SubmitButton } from "@platformx/utilities";
import { ManualCropUsingCdn } from "@platformx/x-image-render";
import { ReactNode, useState } from "react";
import { useTranslation } from "react-i18next";
import { TagSelectorPanel } from "../../../../components/TagSelectorPanel/TagSelectorPanel";
import { ContentTypeTagName } from "../../../../enums/ContentType";
import ArticleImageRender from "../ArticleImageRender/ArticleImageRender";
import { useStyles } from "./PublishModal.styles";

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialogContent-root": {
    padding: theme.spacing(2),
  },
  "& .MuiDialogActions-root": {
    padding: theme.spacing(1),
  },
  // Target the dialog's own paper only; `.Platform-x-Paper-root` also matched the banner image Paper
  // nested inside and gave it the dialog's margin/width.
  "& .Platform-x-Dialog-paper": {
    height: "-webkit-fill-available",
    maxWidth: "none",
    width: "100%",
    [`@media (min-width:${theme.breakpoints.values.sm}px)`]: {
      margin: "0px",
      maxHeight: "calc(100vh - 0px)",
    },
    [`@media (min-width:${theme.breakpoints.values.sm}px)`]: {
      margin: "20px",
      maxHeight: "calc(100vh - 40px)",
    },
  },
}));

export interface DialogTitleProps {
  id: string;
  children?: ReactNode;
  onClose: () => void;
  onPublish: () => void;
  category: any;
  subCategory: any;
  onSave: () => void;
  workflow: object;
  createComment: () => void;
  publishDisabled: boolean;
}

const BootstrapDialogTitle = (props: DialogTitleProps) => {
  const {
    children,
    onClose,
    onPublish,
    category,
    subCategory,
    onSave,
    workflow,
    createComment,
    publishDisabled,
    ...other
  } = props;

  return (
    <DialogTitle
      sx={{
        m: 0,
        p: 2,
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      }}
      {...other}>
      <Box
        onClick={onClose}
        data-testid='publish-modal-close'
        className='backspace'
        sx={{
          position: "absolute",
          left: 15,
        }}>
        <KeyboardBackspaceIcon />
      </Box>
      {children}
      <Box
        sx={{
          position: "absolute",
          right: 15,
        }}>
        <SubmitButton
          category={category}
          subCategory={subCategory}
          workflow={workflow}
          handlePublish={onPublish}
          handleSave={onSave}
          createComment={createComment}
          publishDisabled={publishDisabled}
        />
      </Box>
    </DialogTitle>
  );
};

export default function PublishModal({
  open,
  handleClose,
  content,
  tagData,
  selectedTag,
  selectedTagSm,
  handleTagOnChange,
  onPublish,
  onUploadClick,
  selectedImage,
  state,
  operationType,
  resetSelectedImage,
  updateImageField,
  isUploadArticle,
  category,
  subCategory,
  workflow,
  onSave,
  createComment,
  isEdit,
  publishDisabled,
}) {
  const { t } = useTranslation();
  const classes = useStyles();
  const [manualCropShow, setManualCropShow] = useState(false);

  const doneCropCompleted = (cropHints: any[] = []) => {
    if (cropHints.length > 0) {
      updateImageField({
        published_images: cropHints,
        original_image: state?.ObjectFields?.original_image,
      });
    }
    setManualCropShow(false);
  };

  return (
    <>
      <BootstrapDialog
        onClose={handleClose}
        aria-labelledby='customized-dialog-title'
        open={open}
        sx={{ margin: "0px" }}>
        <BootstrapDialogTitle
          id='customized-dialog-title'
          onClose={handleClose}
          onPublish={onPublish}
          category={category}
          subCategory={subCategory}
          workflow={workflow}
          onSave={onSave}
          createComment={createComment}
          publishDisabled={publishDisabled}>
          {t("submit_text")}
        </BootstrapDialogTitle>

        <DialogContent dividers className={classes.dialogContentStyle}>
          <Box className={classes.innerBoxContent}>
            <Typography variant='h5medium'>{t("better_engagement")}</Typography>
            <Box
              className={classes.publishImgUploadBox}
              sx={{
                width: { sm: "610px", xs: "100%" },
                height: { sm: "343px", xs: "187px" },
              }}>
              {content?.Url ? (
                <>
                  <ArticleImageRender
                    content={selectedImage}
                    imgOrder={{
                      1440: "hero",
                      1280: "landscape",
                      1024: "card2",
                      768: "square",
                      600: "card1",
                      320: "portrait",
                    }}
                    updateField={updateImageField}
                    originalImage={state?.ObjectFields?.original_image}
                    publishedImages={state?.ObjectFields?.published_images}
                    operationType={operationType}
                    resetSelectedImage={resetSelectedImage}
                    isUploadArticle={isUploadArticle}
                  />

                  <Box className={classes.imgBox}>
                    <Box sx={{ display: "flex" }}>
                      <Box
                        sx={{ cursor: "pointer", pr: "7px" }}
                        onClick={() => {
                          handleClose();
                          onUploadClick("Images", "replace");
                        }}>
                        <Box className={classes.cachedIconBox}>
                          <CachedIcon sx={{ color: "#626060" }} />
                        </Box>
                      </Box>
                      <Box
                        sx={{ cursor: "pointer", pl: "7px" }}
                        onClick={() => setManualCropShow(true)}>
                        <Box className={classes.editIconBox}>
                          <ModeEditOutlinedIcon sx={{ color: "#626060" }} />
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                </>
              ) : (
                <Box className={classes.cloudIconBox}>
                  <Box
                    onClick={() => {
                      handleClose();
                      onUploadClick("Images", "choose");
                    }}
                    sx={{ cursor: "pointer" }}>
                    <img src={CloudIcon} alt='img' />
                  </Box>
                  <Typography variant='h4regular' sx={{ textAlign: "center", padding: "15px" }}>
                    {t("drag_drop")}
                  </Typography>
                  <Button
                    variant='quaternaryButton'
                    onClick={() => {
                      handleClose();
                      onUploadClick("Images", "choose");
                    }}
                    data-testid='publish-modal-choose-img'>
                    {t("browse_to_upload")}
                  </Button>
                </Box>
              )}
            </Box>

            <Typography variant='h7medium'>{t("article_banner_note")}</Typography>
            <Box className={classes.dividerStyle}>
              <Divider />
            </Box>
            <Box className={classes.publishTagsWp}>
              <TagSelectorPanel
                isEdit={isEdit}
                tagData={tagData}
                selectedTag={selectedTag}
                selectedTagSm={selectedTagSm}
                handleTagOnChange={handleTagOnChange}
                subCategory={ContentTypeTagName.article}
              />
            </Box>
          </Box>
        </DialogContent>
      </BootstrapDialog>
      {manualCropShow && (
        <ManualCropUsingCdn
          open={manualCropShow}
          backTo={() => setManualCropShow(false)}
          isCrop={true}
          doneCropCompleted={doneCropCompleted}
          originalImage={state?.ObjectFields?.original_image}
          published_images={state?.ObjectFields?.published_images || []}
        />
      )}
    </>
  );
}
